import React, { useCallback, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import qs from "qs";

import * as api from "../api";
import Loader from "../components/Utils/Loader";
import PaymentService from "../components/Payment/PaymentService";
import OfferCountdown from "../components/TableRequests/OfferCountdown";
import TableOfferCard from "../components/TableRequests/TableOfferCard";
import TableRequestGuests from "../components/TableRequests/TableRequestGuests";
import { ReactComponent as AlertIcon } from "../assets/images/input-error.svg";
import { ReactComponent as ClockIcon } from "../assets/images/clock.svg";
import { ReactComponent as CheckIcon } from "../assets/images/success-check.svg";
import { useLoginGate } from "../utils/hooks";
import { formatMinorUnits } from "../utils/money";
import { tableRequestErrorMessage } from "../utils/tableRequest";
import { useTableRequest } from "../utils/useTableRequest";
import { usePaymentConfirmation } from "../utils/usePaymentConfirmation";

const deadlineOptions = {
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short",
};

const formatDeadline = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString([], deadlineOptions);
};

const StatusCard = ({ tone = "neutral", icon, title, children }) => (
  <section
    className={`table-request-status__card table-request-status__card--${tone}`}
  >
    {icon ? (
      <span className="table-request-status__icon" aria-hidden="true">
        {icon}
      </span>
    ) : null}
    <h1 className="table-request-status__card-title">{title}</h1>
    {children}
  </section>
);

const StatusHeader = ({ tone = "neutral", icon, title, children }) => (
  <header
    className={`table-request-status__header table-request-status__header--${tone}`}
  >
    {icon ? (
      <span className="table-request-status__badge" aria-hidden="true">
        {icon}
      </span>
    ) : null}
    <h1 className="table-request-status__title">{title}</h1>
    {children ? <p className="table-request-status__lede">{children}</p> : null}
  </header>
);

const StatusLayout = ({ aside, children }) => (
  <div
    className={`table-request-status__layout${
      aside ? " table-request-status__layout--split" : ""
    }`}
  >
    <div className="table-request-status__main">{children}</div>
    {aside ? <aside className="table-request-status__aside">{aside}</aside> : null}
  </div>
);

const TableRequestStatus = ({
  user = {},
  userLoading = false,
  setShowLogin = () => {},
}) => {
  const { id } = useParams();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const paymentFailed = query.redirect_status === "failed";
  const isSignedIn = useLoginGate(user, userLoading, setShowLogin);
  const { request, state, reload } = useTableRequest(id, isSignedIn);
  const [offerRanOut, setOfferRanOut] = useState(false);
  const [paymentData, setPaymentData] = useState({});
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [payError, setPayError] = useState(
    paymentFailed ? "Your payment didn't go through. You can try again." : ""
  );

  const status = request?.effectiveStatus;
  const confirmation = usePaymentConfirmation({
    active: isSignedIn && query.paid === "1" && !paymentFailed,
    isPaid: status === "paid",
    reload,
  });
  const handleOfferRanOut = useCallback(() => {
    setOfferRanOut(true);
    reload();
  }, [reload]);
  const paying =
    paymentLoading || Boolean(paymentData.clientSecret || paymentData.url);

  const handlePay = () => {
    setPayError("");
    setPaymentLoading(true);
    api.tableRequests
      .payTableRequest(id)
      .then(({ data }) => {
        setPaymentData({
          url: data.cardPaymentUrl,
          paymentSolution: data.paymentSolution,
          clientSecret: data.extraData?.clientSecret,
          orderId: data.order,
        });
      })
      .catch((error) => {
        setPayError(tableRequestErrorMessage(error));
        setPaymentLoading(false);
        reload();
      });
  };

  const page = (announcement, content) => (
    <div className="table-request-status">
      <span className="table-request-status__eyebrow">Your table request</span>
      <p
        className="table-request-status__sr-only"
        role="status"
        aria-live="polite"
      >
        {announcement}
      </p>
      {content}
    </div>
  );

  if (!isSignedIn) {
    return page(
      userLoading ? "" : "Sign in to see your table request",
      userLoading ? (
        <Loader />
      ) : (
        <StatusCard title="Sign in to see your table request">
          <button
            type="button"
            className="btn btn--primary table-request-status__action"
            onClick={() => setShowLogin(true)}
          >
            Sign in
          </button>
        </StatusCard>
      )
    );
  }

  if (state === "loading") {
    return page(
      "",
      <div aria-hidden="true">
        <StatusLayout
          aside={
            <div className="table-request-status__skeleton table-request-status__skeleton--aside" />
          }
        >
          <div className="table-request-status__header">
            <div className="table-request-status__skeleton table-request-status__skeleton--badge" />
            <div className="table-request-status__skeleton table-request-status__skeleton--title" />
            <div className="table-request-status__skeleton table-request-status__skeleton--lede" />
          </div>
        </StatusLayout>
      </div>
    );
  }

  if (state === "not_found") {
    return page(
      "We couldn't find this request",
      <StatusCard tone="danger" title="We couldn't find this request">
        <p className="table-request-status__body">
          The link may be wrong, or the request belongs to another account.
        </p>
        <Link to="/" className="btn btn--secondary table-request-status__action">
          Go to the home page
        </Link>
      </StatusCard>
    );
  }

  if (state === "error") {
    return page(
      "We couldn't load this request",
      <StatusCard tone="danger" title="We couldn't load this request">
        <p className="table-request-status__body">
          Check your connection and try again.
        </p>
        <button
          type="button"
          className="btn btn--secondary table-request-status__action"
          onClick={reload}
        >
          Try again
        </button>
      </StatusCard>
    );
  }

  const offerExpired =
    !paying &&
    (status === "expired" || (status === "accepted" && offerRanOut));
  const heldUntil = formatDeadline(request.expiresAt);
  const { offer } = request;
  const tableName = offer?.tableName || request.tableName;
  const guests = (
    <TableRequestGuests
      user={user}
      guests={request.guests}
      notes={request.notes}
    />
  );

  // A payment that settles just after the deadline still wins on the server
  // (paid outranks expired), so an expiry seen while confirming is not final.
  if (confirmation === "confirming" || confirmation === "timed_out") {
    return page(
      confirmation === "confirming"
        ? "Confirming your payment…"
        : "Your payment is being confirmed. Refresh this page in a minute.",
      confirmation === "confirming" ? (
        <StatusCard title="Confirming your payment…">
          <p className="table-request-status__body">
            This usually takes a few seconds.
          </p>
          <Loader />
        </StatusCard>
      ) : (
        <StatusCard icon={<ClockIcon />} title="Confirming your payment…">
          <p className="table-request-status__body">
            Your payment is being confirmed. Refresh this page in a minute.
          </p>
        </StatusCard>
      )
    );
  }

  if (status === "paid") {
    return page(
      "You're all set",
      <StatusLayout aside={guests}>
        <StatusHeader tone="success" icon={<CheckIcon />} title="You're all set">
          {tableName ? `${tableName} is booked.` : "Your table is booked."} Add
          your guests' details so we can send them their passes.
        </StatusHeader>
        {offer ? <TableOfferCard offer={offer} booked /> : null}
        <div className="table-request-status__actions">
          <Link
            to={`/tables/${request.tableCode}`}
            className="btn btn--primary table-request-status__action"
          >
            Manage your table
          </Link>
        </div>
      </StatusLayout>
    );
  }

  if (offerExpired) {
    return page(
      "This offer has run out",
      <StatusLayout>
        <StatusHeader tone="muted" icon={<ClockIcon />} title="This offer has run out">
          We held {tableName || "your table"}
          {heldUntil ? ` until ${heldUntil}` : " for you"}. If you'd still like
          a table, send us a new request.
        </StatusHeader>
        <div className="table-request-status__actions">
          <Link
            to={`/events/${request.event}/request-table`}
            className="btn btn--secondary table-request-status__action"
          >
            Request a table again
          </Link>
        </div>
      </StatusLayout>
    );
  }

  if (status === "accepted" || paying) {
    const headline = `Good news! ${tableName || "Your table"} is saved for you`;
    return page(
      headline,
      <React.Fragment>
        <StatusLayout aside={guests}>
          <StatusHeader tone="accent" title={headline}>
            {heldUntil
              ? `Pay before ${heldUntil} to make it yours.`
              : "Pay before the timer runs out to make it yours."}
          </StatusHeader>
          <div className="table-request-status__hold">
            <p className="table-request-status__hold-label">Held for you for</p>
            <OfferCountdown
              expiresAt={request.expiresAt}
              onExpire={handleOfferRanOut}
            />
          </div>
          {offer ? <TableOfferCard offer={offer} /> : null}
          <div className="table-request-status__actions table-request-status__actions--sticky">
            {payError ? (
              <div className="table-request-status__error" role="alert">
                <AlertIcon aria-hidden="true" />
                {payError}
              </div>
            ) : null}
            <button
              type="button"
              className="btn btn--primary table-request-status__action table-request-status__pay"
              onClick={handlePay}
              disabled={paymentLoading}
            >
              {offer
                ? `Pay ${formatMinorUnits(offer.total, offer.currency)}`
                : "Pay for your table"}
            </button>
          </div>
        </StatusLayout>
        <PaymentService
          type={paymentData.paymentSolution}
          onClose={() => setPaymentData({})}
          setLoading={setPaymentLoading}
          stripeClientSecret={paymentData.clientSecret}
          isStripeSetup={false}
          stripeRedirectUrl={`/table-requests/${id}?paid=1`}
          cardPaymentUrl={paymentData.url}
          loading={paymentLoading}
        />
      </React.Fragment>
    );
  }

  if (status === "waiting_list") {
    return page(
      "You're on our waiting list",
      <StatusLayout aside={guests}>
        <StatusHeader icon={<ClockIcon />} title="You're on our waiting list">
          Every table that fits your group is taken right now. If one opens up,
          you'll be the first to hear from us.
        </StatusHeader>
      </StatusLayout>
    );
  }

  if (status === "rejected") {
    return page(
      "We couldn't fit you in this time",
      <StatusLayout>
        <StatusHeader tone="muted" title="We couldn't fit you in this time">
          We weren't able to offer a table for this event. We hope to see you at
          the next one.
        </StatusHeader>
      </StatusLayout>
    );
  }

  const thanks = user.firstName ? `Thanks, ${user.firstName}` : "Thanks";
  const received = `${thanks}! We got your request`;
  return page(
    received,
    <StatusLayout aside={guests}>
      <StatusHeader icon={<CheckIcon />} title={received}>
        Our team will look at the floor and email you as soon as we've found
        the right table.
      </StatusHeader>
    </StatusLayout>
  );
};

export default TableRequestStatus;
